import React, { useEffect, useMemo, useState } from 'react';
import { FieldIssue } from '../../../components/wizard/WizardShell';
import {
    PendingTag,
    PrefixIssues,
} from '../../../components/wizard/PendingDict';
import {
    BundleModeRef,
    BundleModeSwitch,
    DeviceBundlePayload,
    DeviceBundlePreflight,
} from '../../../api/bundle';
import { listDict } from '../../../api/dict';
import { listMMGroups, listMMModes, MMGroup, MMMode } from '../../../api/mmode';
import MMGroupEditorModal, {
    newMMGroupDraft,
} from '../../../components/mmode/MMGroupEditorModal';
import MMModeEditorModal, {
    newMMModeDraft,
} from '../../../components/mmode/MMModeEditorModal';
import { alertDialog } from '../../../utils/dialog';
import { ArchTags } from '../../../components/mmode/archs';
import { groupAppliesToArch } from '../../../components/mmode/archUtils';
import { useCan } from '../../../store';
import { IssueByPath } from './useDeviceBundleDraft';
import {
    isPendingGroup,
    isPendingMode,
    removeNewGroup,
    removeNewMode,
    upsertNewGroup,
    upsertNewMode,
    withPendingGroups,
    withPendingModes,
} from './newDicts';

// 设备向导第 ② 步: 加工模式引用 (mm_device_ref).
//
// 勾选这台设备支持哪些加工模式; 别名与速度上下限是**设备级差异** —— 留空就用字典默认值,
// 只有这台机器和别人不一样时才填。交互照搬工况页的「加工模式引用」弹窗, 差别是这里
// 不直接保存, 值进套件草稿, 随整包一次落库。
//
// 模式能力开关 (modeSwitches → device_mode, line/fill/bitmap/cut) 2026-09-22 起也在这一步:
// 它决定"这台机器矢量/填充/位图/切割各开不开、速度上下限多少", 与上面的模式引用同属
// "这台机器会做什么"。速度**只在这里填一次**: 服务端 D9 单源把它 merge 进 mode_capability
// 矩阵 (第 ③ 步那个组里因此禁填 speedMin/speedMax), 工况页与矩阵页读到的是同一份。
// 此前向导没有这段, 6 头机型建完速度全空, 还得逐台去工况页补。
// 模式名来自 device_mode 字典 (服务端不做字典强校验, 与 YAML 导入同口径), 字典外的可自定义添加。
//
// 2026-09-20 就地新建模式值: 新机器带来新模式是常事, 之前得中断向导去「加工模式字典」建完再回来。
// 这里建的是字典里的模式值本体 (mm_mode, 全平台共用), 不是这台设备的引用 —— 建完自动勾上,
// 引用仍走下面的勾选。
// 2026-09-29 起新建的模式值/模式组不再单独提审, 而是记进套件 newDicts 随整包一个工单落库:
// 此前单独提审, 工单没过之前字典里没有这个值, 向导勾不上、只能等审批结束再回来。
// 草稿项在清单里带「随套件新建」标记, 可编辑/撤销; 服务端预检视同已存在, 审批通过后先建字典再建设备。

interface Props {
    payload: DeviceBundlePayload;
    setPayload: (p: DeviceBundlePayload) => void;
    issueByPath: IssueByPath;
}

/** 模式能力开关的速度由服务端 D9 合入的矩阵组 (与后端 bundle.modeCapGroupKey 同口径). */
const MODE_CAP_GROUP = 'mode_capability';

function StepModes({
    payload,
    setPayload,
    issueByPath,
    preflight,
}: Props & { preflight: DeviceBundlePreflight | null }) {
    const canCreateMode = useCan('mm_mode', 'create');
    const [groups, setGroups] = useState<MMGroup[]>([]);
    const [modes, setModes] = useState<MMMode[]>([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState<Partial<MMMode> | null>(null);
    const [groupDraft, setGroupDraft] = useState<Partial<MMGroup> | null>(null);

    useEffect(() => {
        Promise.all([listMMGroups(), listMMModes()])
            .then(([gs, ms]) => {
                setGroups(gs);
                setModes(ms);
            })
            .catch(() => undefined)
            .finally(() => setLoading(false));
    }, []);

    // 字典 + 本套件待新建的草稿项 (newDicts), 下面的过滤/分组/勾选一视同仁
    const nd = payload.newDicts;
    const allGroups = useMemo(() => withPendingGroups(groups, nd), [groups, nd]);
    const allModes = useMemo(() => withPendingModes(modes, nd), [modes, nd]);

    // useMemo 稳住引用 (同 StepCalibration 的 points)
    const refs = useMemo(() => payload.modeRefs || [], [payload.modeRefs]);
    const byValue = useMemo(() => {
        const m: Record<string, BundleModeRef> = {};
        refs.forEach((r) => {
            m[r.modeValue] = r;
        });
        return m;
    }, [refs]);

    const setRefs = (next: BundleModeRef[]) =>
        setPayload({ ...payload, modeRefs: next });

    const toggle = (value: string) => {
        if (byValue[value]) setRefs(refs.filter((r) => r.modeValue !== value));
        else setRefs([...refs, { modeValue: value, enabled: true }]);
    };

    const patchRef = (value: string, p: Partial<BundleModeRef>) =>
        setRefs(refs.map((r) => (r.modeValue === value ? { ...r, ...p } : r)));

    // 只列适用本设备框架的模式组 (mm_group.archCodes, 空 = 不限): 激光机看不到刀切/热压/喷墨那几十个模式.
    // 用户点了"显示全部"时不过滤; 已勾但不适用本框架的模式仍列出.
    const [showAll, setShowAll] = useState(false);
    const archCode = payload.device.archCode;
    const visibleModes = useMemo(() => {
        if (showAll || !archCode) return allModes;
        return allModes.filter((m) => {
            const g = allGroups.find((x) => x.code === m.groupCode);
            return !g || groupAppliesToArch(g.archCodes, archCode) || !!byValue[m.value];
        });
    }, [allModes, allGroups, archCode, showAll, byValue]);
    const hiddenCount = allModes.length - visibleModes.length;

    // 按归属组聚合展示: 同组共享一份参数, 分组能让人看清"这台机器覆盖了哪些档"
    const grouped = useMemo(() => {
        const out: { code: string; group?: MMGroup; modes: MMMode[] }[] = [];
        const seen = new Set<string>();
        visibleModes.forEach((m) => {
            if (seen.has(m.groupCode)) return;
            seen.add(m.groupCode);
            out.push({
                code: m.groupCode,
                group: allGroups.find((g) => g.code === m.groupCode),
                modes: visibleModes.filter((x) => x.groupCode === m.groupCode),
            });
        });
        return out;
    }, [visibleModes, allGroups]);

    // 模式值表单确认: 记进套件 newDicts (不单独提审) 并勾给这台设备; 编辑草稿项时原位替换.
    const onModeDraft = (m: Partial<MMMode> & { value: string }) => {
        if (modes.some((x) => x.value === m.value)) {
            alertDialog(`模式值 ${m.value} 已在字典里, 直接在清单里勾选即可。`);
            return;
        }
        setDraft(null);
        setPayload({
            ...payload,
            newDicts: upsertNewMode(nd, m),
            modeRefs: byValue[m.value]
                ? refs
                : [...refs, { modeValue: m.value, enabled: true }],
        });
    };

    const onGroupDraft = (g: Partial<MMGroup> & { code: string }) => {
        if (groups.some((x) => x.code === g.code)) {
            alertDialog(`模式组 ${g.code} 已在字典里, 直接在下拉里选即可。`);
            return;
        }
        setGroupDraft(null);
        setPayload({ ...payload, newDicts: upsertNewGroup(nd, g) });
        setDraft((cur) => (cur ? { ...cur, groupCode: g.code } : cur));
    };

    const dropPendingGroup = (code: string) => {
        const users = (nd?.modes || []).filter((m) => m.groupCode === code);
        if (users.length) {
            alertDialog(
                `模式组 ${code} 下还有待新建的模式值 ${users.map((m) => m.value).join(', ')}, 先撤销它们。`,
            );
            return;
        }
        setPayload(removeNewGroup(payload, code));
    };

    if (loading)
        return (
            <div className="card p-4 text-sm text-slate-400">加载模式字典…</div>
        );

    return (
        <>
            <div className="infobox text-xs flex items-center gap-2">
                <span>
                    勾选这台设备支持的加工模式
                    (mm_device_mode)。别名与速度是设备级差异, 留空 =
                    用字典默认值。 已勾 {refs.length} 个。
                    {archCode && hiddenCount > 0 && !showAll && (
                        <>
                            {' '}
                            已按本设备框架过滤, 隐藏 {hiddenCount}{' '}
                            个不适用的模式,
                            <button
                                type="button"
                                className="text-brand-600 hover:underline ml-1"
                                onClick={() => setShowAll(true)}
                            >
                                显示全部
                            </button>
                        </>
                    )}
                    {showAll && archCode && (
                        <button
                            type="button"
                            className="text-brand-600 hover:underline ml-1"
                            onClick={() => setShowAll(false)}
                        >
                            只看本框架适用的
                        </button>
                    )}
                </span>
                {canCreateMode && (
                    <button
                        type="button"
                        className="btn-ghost text-xs ml-auto whitespace-nowrap"
                        onClick={() =>
                            setDraft(newMMModeDraft(allGroups[0]?.code || ''))
                        }
                    >
                        + 新建模式值
                    </button>
                )}
            </div>
            <div className="card p-4 space-y-3">
                <div className="text-sm font-semibold">
                    加工模式引用
                    <span className="ml-1.5 text-xs font-normal text-slate-400">
                        按归属组分节 · 同组共享一份参数
                    </span>
                </div>
                {grouped.length === 0 && (
                    <div className="text-xs text-slate-400">
                        没有可选的模式值。
                    </div>
                )}
                {grouped.map(({ code, group, modes: ms }) => (
                    <div className="space-y-1.5" key={code}>
                        <div className="text-xs font-semibold text-slate-600 border-b border-slate-100 pb-1">
                            {group?.nameZh || code}
                            <span className="ml-1.5 font-mono font-normal text-slate-400">
                                {code}
                            </span>
                            <span className="ml-1.5 font-normal inline-flex gap-1 align-middle">
                                <ArchTags codes={group?.archCodes} />
                            </span>
                            {isPendingGroup(nd, code) && (
                                <>
                                    <PendingTag />
                                    <button
                                        type="button"
                                        className="ml-1.5 font-normal text-slate-400 hover:text-red-600"
                                        onClick={() => dropPendingGroup(code)}
                                    >
                                        撤销新建
                                    </button>
                                </>
                            )}
                        </div>
                        <div className="space-y-1.5">
                            {ms.map((m) => {
                                const cur = byValue[m.value];
                                const on = !!cur;
                                return (
                                    <div
                                        key={m.value}
                                        className={`border rounded-lg px-3 py-2 ${
                                            on
                                                ? 'border-brand-200 bg-brand-50/40'
                                                : 'border-slate-100'
                                        }`}
                                    >
                                        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                                            <input
                                                type="checkbox"
                                                checked={on}
                                                onChange={() => toggle(m.value)}
                                            />
                                            <span>{m.nameZh || m.value}</span>
                                            <span className="font-mono text-[11px] text-slate-400">
                                                {m.value}
                                            </span>
                                            {isPendingMode(nd, m.value) && (
                                                <>
                                                    <PendingTag />
                                                    <button
                                                        type="button"
                                                        className="text-xs text-brand-600 hover:underline"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            // id 非 0 = 编辑态 (模式值锁定), 保存仍走 onDraft
                                                            setDraft({
                                                                ...m,
                                                                id: -1,
                                                            });
                                                        }}
                                                    >
                                                        编辑
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="text-xs text-slate-400 hover:text-red-600"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setPayload(
                                                                removeNewMode(
                                                                    payload,
                                                                    m.value,
                                                                ),
                                                            );
                                                        }}
                                                    >
                                                        撤销新建
                                                    </button>
                                                </>
                                            )}
                                        </label>
                                        {on && (
                                            <div className="grid grid-cols-3 gap-2 mt-2 pl-6">
                                                <input
                                                    className="inp h-8 text-xs"
                                                    placeholder="设备级别名 (空=字典名)"
                                                    value={cur.aliasZh || ''}
                                                    onChange={(e) =>
                                                        patchRef(m.value, {
                                                            aliasZh:
                                                                e.target.value,
                                                        })
                                                    }
                                                />
                                                <input
                                                    className="inp h-8 text-xs"
                                                    type="number"
                                                    placeholder="速度下限"
                                                    value={cur.speedMin ?? ''}
                                                    onChange={(e) =>
                                                        patchRef(m.value, {
                                                            speedMin:
                                                                e.target
                                                                    .value ===
                                                                ''
                                                                    ? null
                                                                    : Number(
                                                                          e
                                                                              .target
                                                                              .value,
                                                                      ),
                                                        })
                                                    }
                                                />
                                                <input
                                                    className="inp h-8 text-xs"
                                                    type="number"
                                                    placeholder="速度上限"
                                                    value={cur.speedMax ?? ''}
                                                    onChange={(e) =>
                                                        patchRef(m.value, {
                                                            speedMax:
                                                                e.target
                                                                    .value ===
                                                                ''
                                                                    ? null
                                                                    : Number(
                                                                          e
                                                                              .target
                                                                              .value,
                                                                      ),
                                                        })
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
                <FieldIssue issue={issueByPath['modeRefs']} />
                <PrefixIssues
                    issueByPath={issueByPath}
                    prefixes={['newDicts.modes', 'newDicts.modeGroups']}
                />
            </div>
            {/* 框架没有 mode_capability 组 (如 FDM) 就没有 device_mode 开关可声明, 不铺激光的矢量/填充/位图/切割;
                草稿里已有开关时照常显示, 不让已填的值看不见 */}
            {((preflight?.deviceSchemas || []).some((g) => g.groupKey === MODE_CAP_GROUP) ||
                (payload.modeSwitches || []).length > 0) && (
                <ModeSwitchesCard
                    payload={payload}
                    setPayload={setPayload}
                    issueByPath={issueByPath}
                />
            )}
            {draft && (
                <MMModeEditorModal
                    mode={draft}
                    setMode={setDraft}
                    groups={allGroups}
                    saveText={draft.id ? '确定' : '新建'}
                    hint={
                        <>
                            建的是<b>字典里的模式值</b> (全平台共用),
                            不只是这台设备的引用; 确定后自动勾上这台设备,
                            <b>随本套件一起提交审批</b>, 不用等单独的工单。
                        </>
                    }
                    onCreateGroup={() => setGroupDraft(newMMGroupDraft(archCode ? [archCode] : []))}
                    onClose={() => setDraft(null)}
                    onSaved={() => setDraft(null)}
                    onDraft={onModeDraft}
                />
            )}
            {groupDraft && (
                <MMGroupEditorModal
                    group={groupDraft}
                    setGroup={setGroupDraft}
                    onClose={() => setGroupDraft(null)}
                    onSaved={() => setGroupDraft(null)}
                    onDraft={onGroupDraft}
                />
            )}
        </>
    );
}

/** ModeSwitchesCard 模式能力开关 (device_mode): 行 = device_mode 字典 (+ 自定义), 开关 + 速度区间 + 备注. */
function ModeSwitchesCard({ payload, setPayload, issueByPath }: Props) {
    const [dictModes, setDictModes] = useState<
        { code: string; label: string }[]
    >([]);
    const [custom, setCustom] = useState('');
    useEffect(() => {
        listDict('device_mode')
            .then((es) =>
                setDictModes(es.map((e) => ({ code: e.code, label: e.label }))),
            )
            .catch(() => setDictModes([]));
    }, []);

    const switches = useMemo(
        () => payload.modeSwitches || [],
        [payload.modeSwitches],
    );
    const byMode = useMemo(() => {
        const m: Record<string, BundleModeSwitch> = {};
        switches.forEach((s) => {
            m[s.mode] = s;
        });
        return m;
    }, [switches]);
    const setSwitches = (next: BundleModeSwitch[]) =>
        setPayload({ ...payload, modeSwitches: next });

    const toggle = (mode: string) => {
        if (byMode[mode]) setSwitches(switches.filter((s) => s.mode !== mode));
        else setSwitches([...switches, { mode, isEnabled: true }]);
    };
    const patchSwitch = (mode: string, p: Partial<BundleModeSwitch>) =>
        setSwitches(
            switches.map((s) => (s.mode === mode ? { ...s, ...p } : s)),
        );

    // 字典外但草稿里有的模式名 (自定义 / 恢复的草稿) 也要列出来, 否则填了看不见也删不掉
    const rows = useMemo(() => {
        const out = [...dictModes];
        switches.forEach((s) => {
            if (!out.some((r) => r.code === s.mode))
                out.push({ code: s.mode, label: s.mode });
        });
        return out;
    }, [dictModes, switches]);

    const addCustom = () => {
        const v = custom.trim();
        if (!v || byMode[v]) return;
        setSwitches([...switches, { mode: v, isEnabled: true }]);
        setCustom('');
    };

    const numOrNull = (raw: string) => (raw === '' ? null : Number(raw));

    return (
        <div className="card p-4 space-y-2">
            <div className="text-sm font-semibold flex items-center gap-2">
                模式能力开关
                <span className="text-xs font-normal text-slate-400">
                    device_mode · 已声明 {switches.length} 项
                </span>
            </div>
            <div className="text-[11px] text-slate-400">
                勾上 = 这台设备支持该模式; 速度上下限只在这里填一次,
                提交时自动同步进第 ③ 步的 mode_capability 矩阵
                (那里不必再填速度)。不勾 = 不写 device_mode 行。
            </div>
            <div className="space-y-1.5">
                {rows.map((r) => {
                    const cur = byMode[r.code];
                    const on = !!cur;
                    return (
                        <div
                            key={r.code}
                            className={`border rounded-lg px-3 py-2 ${
                                on
                                    ? 'border-brand-200 bg-brand-50/40'
                                    : 'border-slate-100'
                            }`}
                        >
                            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={on}
                                    onChange={() => toggle(r.code)}
                                />
                                <span>{r.label}</span>
                                {!r.label.includes(r.code) && (
                                    <span className="font-mono text-[11px] text-slate-400">
                                        {r.code}
                                    </span>
                                )}
                            </label>
                            {on && (
                                <div className="grid grid-cols-4 gap-2 mt-2 pl-6">
                                    <label className="flex items-center gap-1.5 text-xs text-slate-600">
                                        <input
                                            type="checkbox"
                                            checked={cur.isEnabled}
                                            onChange={(e) =>
                                                patchSwitch(r.code, {
                                                    isEnabled: e.target.checked,
                                                })
                                            }
                                        />
                                        启用
                                    </label>
                                    <input
                                        className="inp h-8 text-xs"
                                        type="number"
                                        placeholder="速度下限 mm/s"
                                        value={cur.speedMin ?? ''}
                                        onChange={(e) =>
                                            patchSwitch(r.code, {
                                                speedMin: numOrNull(
                                                    e.target.value,
                                                ),
                                            })
                                        }
                                    />
                                    <input
                                        className="inp h-8 text-xs"
                                        type="number"
                                        placeholder="速度上限 mm/s"
                                        value={cur.speedMax ?? ''}
                                        onChange={(e) =>
                                            patchSwitch(r.code, {
                                                speedMax: numOrNull(
                                                    e.target.value,
                                                ),
                                            })
                                        }
                                    />
                                    <input
                                        className="inp h-8 text-xs"
                                        placeholder="备注 (来源/待确认)"
                                        value={cur.note || ''}
                                        onChange={(e) =>
                                            patchSwitch(r.code, {
                                                note: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="flex items-center gap-2">
                <input
                    className="inp h-8 text-xs w-56 font-mono"
                    placeholder="字典外的模式名 (如 print)"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addCustom()}
                />
                <button
                    type="button"
                    className="btn-ghost text-xs"
                    onClick={addCustom}
                    disabled={!custom.trim()}
                >
                    + 自定义模式
                </button>
            </div>
            <FieldIssue issue={issueByPath['modeSwitches']} />
        </div>
    );
}

export default StepModes;
